import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Card,
  CardContent,
  Divider,
  useTheme,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Backdrop,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import { Edit, Save, Cancel } from '@mui/icons-material';
import { Logger } from '../../../utils/Logger';
import { useSelector } from 'react-redux';
import { RootState } from '../../../state/store';
import { FlexBetween } from '../../../components/FlexBetween';
import { useModulePermissions } from '../../../hooks/useModulePermissions';
import { TelegramConfig } from '../../Interfaces/Company/Company';
import { apiClient } from '../../../utils/ApiClient';
import { API_BASE_URL } from '../../../envConfig';

export const Telegram: React.FC = () => {
  const theme = useTheme();
  const user = useSelector((state: RootState) => state.authentication.user);
  const { hasInstantUpdateAccess, getInstantUpdateRole, isUserSuperAdmin } = useModulePermissions();

  const [configuration, setConfiguration] = useState<TelegramConfig | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const channelName = "Telegram";
  const userRole = getInstantUpdateRole();
  const canEdit = isUserSuperAdmin || userRole === 'admin';
  const canView = hasInstantUpdateAccess() || isUserSuperAdmin;

  // Check if user has access to this module
  if (!canView) {
    return (
      <Box pl={{ xs: 1.25, sm: 2.5 }} pt={1} width="100%">
        <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
          Access Denied
        </Typography>
        <Typography variant="body1" color="textSecondary">
          You don't have permission to access Telegram configuration.
        </Typography>
      </Box>
    );
  }

  // Function to fetch configuration
  const fetchConfiguration = async () => {
    if (!user?.companyId) {
      Logger.warn("src/modules/Settings/Telegram/Telegram.tsx : fetchConfiguration()", "User companyId is not available.");
      return;
    }
    setLoading(true);
    try {
      // Use ApiClient instead of fetch (automatically includes Authorization header)
      const data = await apiClient.get(`/configuration/${user.companyId}/telegram`);
      // Backend now returns data directly in the data field
      const configData = data.data || null;
      setConfiguration(configData);
      Logger.info("src/modules/Settings/Telegram/Telegram.tsx : fetchConfiguration()", `Fetched configuration: ${JSON.stringify(configData)}`);
    } catch (error) {
      Logger.error("src/modules/Settings/Telegram/Telegram.tsx : fetchConfiguration()", `Failed to fetch configuration: ${error}`);
      setSnackbar({ open: true, message: `Failed to load configuration: ${error instanceof Error ? error.message : String(error)}`, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfiguration();
  }, [user?.companyId]);

  const handleOpenDialog = () => {
    if (!canEdit) return;
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    fetchConfiguration();
  };

  const handleSaveConfiguration = async (configData: TelegramConfig) => {
    if (!user?.companyId || !canEdit) return;

    try {
      setLoading(true);
      // Use ApiClient instead of fetch (automatically includes Authorization header)
      await apiClient.put(`/configuration/${user.companyId}/telegram`, configData);
      
      setSnackbar({ open: true, message: 'Telegram configuration saved successfully!', severity: 'success' });
      handleCloseDialog();
    } catch (error) {
      Logger.error("src/modules/Settings/Telegram/Telegram.tsx : handleSaveConfiguration()", `Failed to save configuration: ${error}`);
      setSnackbar({ open: true, message: `Operation failed: ${error instanceof Error ? error.message : String(error)}`, severity: 'error' });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box pl={{ xs: 1.25, sm: 2.5 }} pt={1} width="100%">
      {loading && (
        <Backdrop sx={{ color: theme.palette.secondary.main, zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        {channelName} Integration
      </Typography>

      <Box display="flex" flexWrap="wrap" gap={3} sx={{ maxWidth: 800, margin: 'auto' }}>
        <Box sx={{ width: '100%' }}>
          <Card sx={{ p: 3, borderRadius: '12px', boxShadow: 3, height: '100%' }}>
            <CardContent>
              <Box 
                sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: { xs: 'center', sm: 'space-between' },
                  alignItems: { xs: 'center', sm: 'center' },
                  gap: { xs: 2, sm: 0 },
                  mb: 2 
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    mb: { xs: 0, sm: 1 },
                    textAlign: { xs: 'center', sm: 'left' },
                    color: theme.palette.heading?.main ?? theme.palette.secondary.main,
                  }}
                >
                  Configured Credentials
                </Typography>
                {canEdit && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<Edit />}
                    onClick={handleOpenDialog}
                    sx={{ alignSelf: { xs: 'center', sm: 'center' } }}
                  >
                    {configuration ? 'Edit Configuration' : 'Add Configuration'}
                  </Button>
                )}
              </Box>

              <Divider sx={{ my: 3 }} />

              {configuration ? (
                <Box display="flex" flexWrap="wrap" gap={3}>
                  <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
                    <TextField
                      label="Access Token"
                      value={configuration.accessToken ? '********' : 'Not Set'}
                      fullWidth
                      margin="normal"
                      InputProps={{ readOnly: true }}
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                  <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
                    <TextField
                      label="Bot Token"
                      value={configuration.botToken || 'Not Set'}
                      fullWidth
                      margin="normal"
                      InputProps={{ readOnly: true }}
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                  <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
                    <TextField
                      label="Chat ID"
                      value={configuration.chatId || 'Not Set'}
                      fullWidth
                      margin="normal"
                      InputProps={{ readOnly: true }}
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                  <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
                    <TextField
                      label="Recipient Number (for testing)"
                      value={configuration.recipientNumber || 'Not Set'}
                      fullWidth
                      margin="normal"
                      InputProps={{ readOnly: true }}
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                  <Box sx={{ width: '100%' }}>
                    <TextField
                      label="Test Message"
                      value={configuration.testMessage || 'Not Set'}
                      fullWidth
                      margin="normal"
                      InputProps={{ readOnly: true }}
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                  <Box sx={{ width: '100%' }}>
                    <TextField
                      label="Status"
                      value={configuration.isActive ? 'Active' : 'Inactive'}
                      fullWidth
                      margin="normal"
                      InputProps={{ readOnly: true }}
                      variant="outlined"
                      size="small"
                      color={configuration.isActive ? 'success' : 'error'}
                    />
                  </Box>
                </Box>
              ) : (
                <Typography variant="body1" color="textSecondary" sx={{ textAlign: 'center', py: 5 }}>
                  No {channelName} configuration found. {canEdit ? 'Click "Add Configuration" to get started.' : 'Contact your administrator to configure Telegram.'}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Configuration Dialog */}
      {canEdit && (
        <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="md">
          <DialogTitle>{configuration ? `Edit ${channelName} Configuration` : `Add New ${channelName} Configuration`}</DialogTitle>
          <DialogContent>
            <ConfigurationForm
              configuration={configuration}
              onSave={handleSaveConfiguration}
              onCancel={handleCloseDialog}
            />
          </DialogContent>
          <DialogActions>
            <Button 
              variant="outlined"
              color="secondary" 
              onClick={handleCloseDialog} 
              startIcon={<Cancel />}
            >
              Cancel
            </Button>
            <Button 
              onClick={() => document.getElementById('telegram-configuration-form')?.dispatchEvent(new Event('submit', { bubbles: true }))}
              startIcon={<Save />} 
              variant="contained" 
              color="success"
            >
              Save Configuration
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Configuration Form Component
interface ConfigurationFormProps {
  configuration: TelegramConfig | null;
  onSave: (config: TelegramConfig) => Promise<void>;
  onCancel: () => void;
}

const ConfigurationForm: React.FC<ConfigurationFormProps> = ({ configuration, onSave, onCancel: _onCancel }) => {
  const [formData, setFormData] = useState<TelegramConfig>({
    accessToken: configuration?.accessToken || '',
    botToken: configuration?.botToken || '',
    chatId: configuration?.chatId || '',
    recipientNumber: configuration?.recipientNumber || '',
    testMessage: configuration?.testMessage || '',
    isActive: configuration?.isActive || false,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave(formData);
  };

  return (
    <Box component="form" id="telegram-configuration-form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <TextField
        label="Access Token"
        value={formData.accessToken}
        onChange={(e) => setFormData({ ...formData, accessToken: e.target.value })}
        fullWidth
        margin="normal"
        required
        variant="outlined"
      />
      <TextField
        label="Bot Token"
        value={formData.botToken}
        onChange={(e) => setFormData({ ...formData, botToken: e.target.value })}
        fullWidth
        margin="normal"
        required
        variant="outlined"
      />
      <TextField
        label="Chat ID"
        value={formData.chatId}
        onChange={(e) => setFormData({ ...formData, chatId: e.target.value })}
        fullWidth
        margin="normal"
        required
        variant="outlined"
      />
      <TextField
        label="Recipient Number (for testing)"
        value={formData.recipientNumber}
        onChange={(e) => setFormData({ ...formData, recipientNumber: e.target.value })}
        fullWidth
        margin="normal"
        required
        variant="outlined"
      />
      <TextField
        label="Test Message"
        value={formData.testMessage}
        onChange={(e) => setFormData({ ...formData, testMessage: e.target.value })}
        fullWidth
        margin="normal"
        required
        variant="outlined"
        multiline
        rows={3}
      />
    </Box>
  );
};
